import { Check, CreditCard, Lock } from "lucide-react";
import { useI18n } from "../../i18n";
import { fmtMoney } from "../../lib/money";

export const PLAN_ORDER = ["free", "basic", "pro", "agency"];

const BRANDS = { visa: "Visa", mastercard: "Mastercard", amex: "American Express", discover: "Discover", jcb: "JCB", unionpay: "UnionPay", diners: "Diners", cartes_bancaires: "Cartes Bancaires" };

export const methodLabel = (t, m) => {
  if (!m) return null;
  if (m.type === "card") return [BRANDS[m.brand] || t("billing.pm_card"), m.last4 && `•••• ${m.last4}`].filter(Boolean).join(" ");
  if (m.type === "paypal") return "PayPal";
  return m.type.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
};

export const PayMethods = ({ className = "" }) => {
  const { t } = useI18n();
  return (
    <div className={`am-paymethods ${className}`} data-testid="accepted-payment-methods">
      <Lock className="h-4 w-4 shrink-0" />
      <span>{t("pricing.acceptedMethods")}</span>
      <span className="am-paybadges" dir="ltr">
        <span className="am-paybadge" data-testid="paymethod-card"><CreditCard className="h-3.5 w-3.5" />{t("billing.pm_card")}</span>
        <span className="am-paybadge is-paypal" data-testid="paymethod-paypal">PayPal</span>
      </span>
    </div>
  );
};

export const IntervalToggle = ({ value, onChange }) => {
  const { t } = useI18n();
  return (
    <div className="am-toggle" role="group" data-testid="interval-toggle">
      {["monthly", "annual"].map((k) => (
        <button key={k} type="button" className={value === k ? "is-on" : ""} aria-pressed={value === k} onClick={() => onChange(k)} data-testid={`interval-${k}`}>
          <span>{t(`pricing.${k}`)}</span>{k === "annual" && <span className="am-save">{t("pricing.save20")}</span>}
        </button>
      ))}
    </div>
  );
};

export const PlanCards = ({ plans, interval, current, onSelect, busy, paid = false }) => {
  const { t, locale, fmtNum } = useI18n();
  return (
    <div className="am-plans" data-testid="plan-cards">
      {PLAN_ORDER.map((key) => {
        const p = plans[key];
        const price = interval === "annual" ? p.annual : p.monthly;
        const isCurrent = current === key;
        return (
          <article key={key} className={`am-plan ${key === "pro" ? "is-featured" : ""}`} data-testid={`plan-card-${key}`}>
            {key === "pro" && <span className="am-plan-flag">{t("pricing.popular")}</span>}
            <h2 className="am-plan-name">{t(`plans.${key}`)}</h2>
            <p className="am-plan-price" data-testid={`plan-price-${key}`}>{fmtMoney(locale, price, "EUR")}<span>{t("pricing.perMonth")}</span></p>
            <p className="am-hint m-0" data-testid={`plan-billed-${key}`}>{key === "free" ? t("pricing.forever") : t(interval === "annual" ? "pricing.billedYearly" : "pricing.billedMonthly",
              { total: fmtMoney(locale, interval === "annual" ? p.annual * 12 : p.monthly, "EUR") })}</p>
            <ul className="am-plan-list">
              <li data-testid={`plan-credits-${key}`}><Check className="h-4 w-4" /><span>{t(key === "free" ? "pricing.creditsOnce" : "pricing.creditsMonth", { count: fmtNum(p.credits) })}</span></li>
              <li><Check className="h-4 w-4" /><span>{p.mailboxes ? t("pricing.mailboxes", { count: p.mailboxes }) : t("pricing.noMailbox")}</span></li>
              <li><Check className="h-4 w-4" /><span>{t(key === "free" ? "pricing.featFree" : "pricing.featPaid")}</span></li>
              {key !== "free" && <li><Check className="h-4 w-4" /><span>{t("pricing.rollover")}</span></li>}
            </ul>
            <button type="button" className={`am-btn ${key === "pro" ? "am-btn-primary" : "am-btn-ghost"} w-full justify-center`} disabled={busy || isCurrent}
                    onClick={() => onSelect(key)} data-testid={`plan-cta-${key}`}>
              <span>{isCurrent ? t("billing.currentPlan") : key === "free" ? t(paid ? "billing.manage" : "pricing.startFree") : t("pricing.choose", { plan: t(`plans.${key}`) })}</span>
            </button>
          </article>
        );
      })}
    </div>
  );
};
